import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../../store/auth';
import { enrollmentsApi, meetingsApi, usersApi, activitiesApi, DailyActivity, ActivityData } from '../../lib/api';
import { SectionLoader } from '../../components/ui/PremiumLoader';

// Premium Icons
const IconBook = ({ className = '', style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
    <path d="M8 7h6" />
  </svg>
);

const IconGraduation = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
    <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5" />
  </svg>
);

const IconAward = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="6" />
    <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
  </svg>
);

const IconCalendar = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
    <line x1="16" x2="16" y1="2" y2="6" />
    <line x1="8" x2="8" y1="2" y2="6" />
    <line x1="3" x2="21" y1="10" y2="10" />
  </svg>
);

const IconChevronRight = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const IconMoreVertical = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="12" cy="19" r="1.5" />
  </svg>
);

const IconRadio = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="2" />
    <path d="M4.93 19.07a10 10 0 0 1 0-14.14" />
    <path d="M7.76 16.24a6 6 0 0 1 0-8.48" />
    <path d="M16.24 7.76a6 6 0 0 1 0 8.48" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
  </svg>
);

import { joinMeetingSecure, buildJitsiJoinUrl } from '../../lib/jitsi';


interface Stats {
  totalEnrollments: number;
  completedCourses: number;
  inProgressCourses: number;
  totalSpent: number;
  totalReviews: number;
}

interface Enrollment {
  id: string;
  progress: number;
  courseId: string;
  course: {
    id: string;
    title: string;
    slug: string;
    thumbnailUrl?: string;
    totalLessons: number;
    level?: string;
    courseType?: 'RECORDED' | 'LIVE';
    professor: {
      firstName: string;
      lastName: string;
    };
  };
  // Live meeting info (populated from meetings data)
  activeMeeting?: Meeting;
}

interface Meeting {
  id: string;
  title: string;
  scheduledAt: string;
  duration: number;
  status: 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED';
  jitsiRoomId: string;
  meetingType: 'SESSION' | 'QA_FINALE';
  course: {
    id: string;
    title: string;
  };
  professor?: {
    firstName: string;
    lastName: string;
  };
}

// Course color themes
const courseColors = [
  { bg: '#6366f1', light: 'rgba(99, 102, 241, 0.15)' },
  { bg: '#ec4899', light: 'rgba(236, 72, 153, 0.15)' },
  { bg: '#f59e0b', light: 'rgba(245, 158, 11, 0.15)' },
  { bg: '#10b981', light: 'rgba(16, 185, 129, 0.15)' },
  { bg: '#8b5cf6', light: 'rgba(139, 92, 246, 0.15)' },
];

// Activity Chart - Default data for empty state
const defaultActivityData: DailyActivity[] = (() => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const today = new Date();
  const result: DailyActivity[] = [];
  
  // Generate last 7 days
  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dayIndex = date.getDay();
    // Convert Sunday (0) to end of week, Mon (1) -> 0, etc.
    const dayName = days[dayIndex === 0 ? 6 : dayIndex - 1];
    
    result.push({
      date: date.toISOString().split('T')[0],
      day: dayName,
      minutes: 0,
      hours: 0,
    });
  }
  
  return result;
})();

// Placeholder heights for visual appeal (percentage of max height)
const placeholderHeights = [35, 55, 45, 65, 50, 60, 30];

interface ActivityChartProps {
  data: DailyActivity[];
  totalHours: number;
  isLoading?: boolean;
}

function ActivityChart({ data, totalHours, isLoading }: ActivityChartProps) {
  // Use default data if no real data provided
  const chartData = data.length > 0 ? data : defaultActivityData;
  const hasRealData = data.length > 0 && data.some(d => d.hours > 0);
  const maxHours = Math.max(...chartData.map(d => d.hours), 0.1);
  
  // Get today's day name
  const today = new Date().toLocaleDateString('en-US', { weekday: 'short' });
  
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-baseline justify-between">
          <div className="h-8 w-16 bg-white/5 rounded animate-pulse" />
          <div className="h-4 w-12 bg-white/5 rounded animate-pulse" />
        </div>
        <div className="flex items-end justify-between gap-2 h-20">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="flex flex-col items-center gap-2 flex-1">
              <div className="w-full max-w-[20px] h-8 bg-white/5 rounded-md animate-pulse" />
              <div className="h-3 w-6 bg-white/5 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <div>
          <span className="text-3xl font-bold text-white tracking-tight">{totalHours.toFixed(1)}</span>
          <span className="text-lg text-white/40 ml-1">H</span>
        </div>
        <button className="flex items-center gap-1 text-xs text-white/40 hover:text-white/60 transition-colors">
          <span>Weekly</span>
          <IconChevronRight className="w-3 h-3" />
        </button>
      </div>
      
      <div className="flex items-end justify-between gap-2 h-20">
        {chartData.map((dayData, index) => {
          const isToday = dayData.day === today;
          const hasActivity = dayData.hours > 0;
          
          // Calculate height - use real data if available, otherwise use placeholder
          let height: number;
          if (hasRealData) {
            height = hasActivity ? (dayData.hours / maxHours) * 100 : 15; // Min 15% for days with no activity
          } else {
            // Use placeholder heights for empty state to look visually appealing
            height = placeholderHeights[index];
          }
          
          return (
            <div key={dayData.date || index} className="flex flex-col items-center gap-2 flex-1">
              <div className="relative w-full flex justify-center">
                {isToday && hasActivity && (
                  <span className="absolute -top-5 text-[10px] text-white/50 whitespace-nowrap font-medium">
                    {dayData.hours}h
                  </span>
                )}
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${height}%` }}
                  transition={{ delay: index * 0.05 + 0.3, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="w-full max-w-[20px] rounded-md"
                  style={{
                    background: isToday && hasRealData
                      ? 'linear-gradient(180deg, #a855f7 0%, #7c3aed 100%)'
                      : 'rgba(168, 85, 247, 0.25)',
                    minHeight: '6px'
                  }}
                />
              </div>
              <span className={`text-[10px] font-medium ${isToday ? 'text-white/60' : 'text-white/30'}`}>
                {dayData.day}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function StudentDashboard() {
  const { user } = useAuthStore();
  const [stats, setStats] = useState<Stats | null>(null);
  const [recentEnrollments, setRecentEnrollments] = useState<Enrollment[]>([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState<Meeting[]>([]);
  const [activityData, setActivityData] = useState<ActivityData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActivityLoading, setIsActivityLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, enrollmentsRes, meetingsRes] = await Promise.all([
          usersApi.getStats(),
          enrollmentsApi.getAll({ status: 'ACTIVE' }),
          meetingsApi.getUpcoming({ limit: 20 }), // Get more meetings to map to courses
        ]);

        if (statsRes.data.success) {
          setStats(statsRes.data.data as Stats);
        }
        
        const meetings = meetingsRes.data.success ? (meetingsRes.data.data as Meeting[]) : [];
        setUpcomingMeetings(meetings.slice(0, 3)); // Show top 3 in sidebar
        
        if (enrollmentsRes.data.success) {
          const enrollmentsData = enrollmentsRes.data.data as Enrollment[];
          
          // Enrich enrollments with active meeting info for live courses
          const enrichedEnrollments = enrollmentsData.map(enrollment => {
            if (enrollment.course.courseType === 'LIVE') {
              // Find active (LIVE) or next scheduled meeting for this course
              const activeMeeting = meetings.find(
                m => m.course.id === enrollment.course.id && m.status === 'LIVE'
              ) || meetings.find(
                m => m.course.id === enrollment.course.id && m.status === 'SCHEDULED'
              );
              return { ...enrollment, activeMeeting };
            }
            return enrollment;
          });
          
          setRecentEnrollments(enrichedEnrollments.slice(0, 5));
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    const fetchActivity = async () => {
      try {
        const activityRes = await activitiesApi.get({ period: 'week' });
        if (activityRes.data.success && activityRes.data.data) {
          setActivityData(activityRes.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch activity data:', error);
        // Set default empty activity data
        setActivityData({
          period: 'week',
          startDate: new Date().toISOString(),
          endDate: new Date().toISOString(),
          totalMinutes: 0,
          totalHours: 0,
          averageMinutesPerDay: 0,
          dailyActivity: [],
        });
      } finally {
        setIsActivityLoading(false);
      }
    };
    
    fetchData();
    fetchActivity();
  }, []);

  if (isLoading) {
    return <SectionLoader className="h-full" />;
  }

  const getStatusBadge = (progress: number) => {
    if (progress === 100) {
      return { label: 'Completed', color: '#10b981' };
    }
    if (progress > 0) {
      return { label: 'On Progress', color: '#f59e0b' };
    }
    return { label: 'Not Started', color: '#6b7280' };
  };

  const totalLessons = recentEnrollments.reduce((sum, e) => sum + (e.course.totalLessons || 0), 0);

  return (
    <div className="h-full flex flex-col xl:flex-row gap-6">
      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 space-y-6 overflow-x-hidden">
        {/* Welcome Header */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-2xl font-semibold text-white tracking-tight">
            Welcome back, {user?.firstName} <span className="text-2xl">👋</span>
          </h1>
        </motion.div>

        {/* Hero Banner - Full Width, No Illustration */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="relative overflow-hidden rounded-2xl p-6 sm:p-8 lg:p-10"
          style={{ 
            background: 'linear-gradient(135deg, #0c1929 0%, #162544 50%, #1e3a5f 100%)',
          }}
        >
          {/* Subtle grid pattern */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
          
          {/* Decorative elements */}
          <div className="absolute top-6 right-8 text-white/10 text-3xl">★</div>
          <div className="absolute bottom-6 right-20 w-20 h-20 rounded-full bg-gradient-to-br from-coral/20 to-transparent blur-2xl" />
          
          <div className="relative z-10">
            <motion.span 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="inline-block px-4 py-1.5 text-xs font-semibold tracking-wider uppercase rounded-full mb-6"
              style={{ 
                background: 'rgba(220, 70, 74, 0.2)',
                color: '#f87171',
                border: '1px solid rgba(220, 70, 74, 0.3)'
              }}
            >
              Online Course
            </motion.span>
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white leading-tight tracking-tight mb-6 sm:mb-8 max-w-2xl"
            >
              Upgrade Your Skills with Expert Online Courses
            </motion.h2>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Link 
                to="/courses" 
                className="inline-flex items-center gap-2 px-7 py-3.5 
                         font-semibold text-sm rounded-xl
                         transition-all duration-300 hover:scale-105"
                style={{ 
                  background: '#ffffff',
                  color: '#0c1929',
                  boxShadow: '0 4px 20px rgba(255,255,255,0.2)'
                }}
              >
                See All
              </Link>
            </motion.div>
          </div>
        </motion.div>

        {/* Stats Row - Increased Height */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4"
        >
          {[
            { 
              label: 'Enrolled Course', 
              value: stats?.totalEnrollments || 0, 
              Icon: IconBook, 
              gradient: 'linear-gradient(135deg, rgba(56, 189, 248, 0.12) 0%, rgba(59, 130, 246, 0.08) 100%)',
              iconBg: 'linear-gradient(135deg, #38bdf8 0%, #3b82f6 100%)',
              border: 'rgba(56, 189, 248, 0.15)'
            },
            { 
              label: 'Lesson', 
              value: totalLessons, 
              Icon: IconGraduation, 
              gradient: 'linear-gradient(135deg, rgba(244, 114, 182, 0.12) 0%, rgba(236, 72, 153, 0.08) 100%)',
              iconBg: 'linear-gradient(135deg, #f472b6 0%, #ec4899 100%)',
              border: 'rgba(244, 114, 182, 0.15)'
            },
            { 
              label: 'Certificate', 
              value: stats?.completedCourses || 0, 
              Icon: IconAward, 
              gradient: 'linear-gradient(135deg, rgba(167, 139, 250, 0.12) 0%, rgba(139, 92, 246, 0.08) 100%)',
              iconBg: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)',
              border: 'rgba(167, 139, 250, 0.15)'
            },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.08, duration: 0.5 }}
              className="relative overflow-hidden p-6 rounded-2xl"
              style={{ 
                background: stat.gradient,
                border: `1px solid ${stat.border}`,
                minHeight: '120px'
              }}
            >
              <div className="flex items-center gap-5">
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{ background: stat.iconBg, boxShadow: `0 4px 16px rgba(0,0,0,0.2)` }}
                >
                  <stat.Icon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <div className="text-4xl font-bold text-white tracking-tight">{stat.value}</div>
                  <div className="text-sm text-white/40 font-medium mt-1">{stat.label}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* My Class List */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex-1 flex flex-col min-h-0"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white tracking-tight">My class list</h2>
            <Link 
              to="/student/my-courses" 
              className="px-5 py-2.5 text-sm font-semibold rounded-xl transition-all duration-300 hover:scale-105"
              style={{ 
                background: '#ffffff',
                color: '#0a0a0f',
                boxShadow: '0 2px 10px rgba(255,255,255,0.1)'
              }}
            >
              See All
            </Link>
          </div>

          <div 
            className="flex-1 rounded-2xl overflow-hidden"
            style={{ 
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.04)'
            }}
          >
            {recentEnrollments.length > 0 ? (
              <>
                {/* Table Header - Hidden on mobile */}
                <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <div className="col-span-5 text-xs font-semibold text-white/30 uppercase tracking-wider">Class</div>
                  <div className="col-span-4 text-xs font-semibold text-white/30 uppercase tracking-wider">Progress</div>
                  <div className="col-span-3 text-xs font-semibold text-white/30 uppercase tracking-wider">Status</div>
                </div>
                
                {/* Table Body */}
                <div className="divide-y divide-white/[0.04]">
                  {recentEnrollments.map((enrollment, index) => {
                    const status = getStatusBadge(enrollment.progress);
                    const colorTheme = courseColors[index % courseColors.length];
                    const isLiveCourse = enrollment.course.courseType === 'LIVE';
                    const activeMeeting = enrollment.activeMeeting;
                    const isLive = activeMeeting?.status === 'LIVE';
                    const hasScheduledMeeting = activeMeeting?.status === 'SCHEDULED';
                    
                    // For live courses, link to course details (where sessions are shown)
                    // For recorded courses, link to course player
                    const courseLink = isLiveCourse
                      ? `/courses/${enrollment.course.slug}`
                      : `/student/course/${enrollment.id}`;
                    
                    const handleJoinMeeting = async (e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (activeMeeting) {
                        try {
                          await joinMeetingSecure(activeMeeting.id);
                        } catch {
                          window.open(buildJitsiJoinUrl(activeMeeting.jitsiRoomId), '_blank');
                        }
                      }
                    };
                    
                    return (
                      <Link
                        key={enrollment.id}
                        to={courseLink}
                        className={`flex flex-col md:grid md:grid-cols-12 gap-3 md:gap-4 px-4 md:px-6 py-4 hover:bg-white/[0.02] transition-colors group ${
                          isLive ? 'bg-gradient-to-r from-red-500/5 to-transparent' : ''
                        }`}
                      >
                        {/* Course Info */}
                        <div className="md:col-span-5 flex items-center gap-3 md:gap-4">
                          <div 
                            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 relative"
                            style={{ background: isLive ? 'rgba(239, 68, 68, 0.15)' : isLiveCourse ? 'rgba(147, 51, 234, 0.15)' : colorTheme.light }}
                          >
                            {enrollment.course.thumbnailUrl ? (
                              <img 
                                src={enrollment.course.thumbnailUrl} 
                                alt="" 
                                className="w-full h-full rounded-xl object-cover"
                              />
                            ) : isLiveCourse ? (
                              <IconRadio className={`w-5 h-5 ${isLive ? 'text-red-400' : 'text-purple-400'}`} />
                            ) : (
                              <IconBook className="w-5 h-5" style={{ color: colorTheme.bg }} />
                            )}
                            {/* Live indicator overlay */}
                            {isLive && (
                              <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse border-2 border-[#0a0a0f]" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-white text-sm truncate group-hover:text-coral transition-colors">
                                {enrollment.course.title}
                              </h3>
                              {isLive ? (
                                <span className="flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-500/20 text-red-400 border border-red-500/30 animate-pulse">
                                  <IconRadio className="w-2.5 h-2.5" />
                                  LIVE NOW
                                </span>
                              ) : isLiveCourse && (
                                <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30">
                                  LIVE
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-white/30 mt-0.5">
                              {isLive && activeMeeting 
                                ? activeMeeting.title
                                : `${enrollment.course.level || 'Beginner'} • ${isLiveCourse ? 'Live Sessions' : `${enrollment.course.totalLessons || 0} lessons`}`
                              }
                            </p>
                          </div>
                        </div>
                        
                        {/* Progress / Meeting Info */}
                        <div className="md:col-span-4 flex items-center gap-3 pl-14 md:pl-0">
                          {isLive ? (
                            <button
                              onClick={handleJoinMeeting}
                              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
                            >
                              <IconRadio className="w-4 h-4" />
                              Join Now
                            </button>
                          ) : isLiveCourse && hasScheduledMeeting && activeMeeting ? (
                            <div className="flex items-center gap-2 text-amber-400">
                              <IconCalendar className="w-4 h-4" />
                              <span className="text-sm font-medium">
                                {new Date(activeMeeting.scheduledAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {new Date(activeMeeting.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          ) : isLiveCourse ? (
                            <div className="flex items-center gap-2 text-purple-400">
                              <IconCalendar className="w-4 h-4" />
                              <span className="text-sm font-medium">View Sessions</span>
                            </div>
                          ) : (
                            <>
                              <span className="text-sm text-white/50 font-medium w-20">{Math.round(enrollment.progress)}% Finish</span>
                              <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                                <motion.div 
                                  initial={{ width: 0 }}
                                  animate={{ width: `${enrollment.progress}%` }}
                                  transition={{ delay: 0.5 + index * 0.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                                  className="h-full rounded-full"
                                  style={{ background: colorTheme.bg }}
                                />
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Status */}
                        <div className="md:col-span-3 flex items-center pl-14 md:pl-0">
                          {isLive ? (
                            <span 
                              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
                              style={{ 
                                background: 'rgba(239, 68, 68, 0.15)',
                                color: '#ef4444'
                              }}
                            >
                              <span 
                                className="w-1.5 h-1.5 rounded-full animate-pulse"
                                style={{ background: '#ef4444' }}
                              />
                              Live Now
                            </span>
                          ) : isLiveCourse ? (
                            <span 
                              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
                              style={{ 
                                background: 'rgba(147, 51, 234, 0.15)',
                                color: '#a855f7'
                              }}
                            >
                              <span 
                                className="w-1.5 h-1.5 rounded-full"
                                style={{ background: '#a855f7' }}
                              />
                              Enrolled
                            </span>
                          ) : (
                            <span 
                              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
                              style={{ 
                                background: `${status.color}15`,
                                color: status.color
                              }}
                            >
                              <span 
                                className="w-1.5 h-1.5 rounded-full"
                                style={{ background: status.color }}
                              />
                              {status.label}
                            </span>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center py-16">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
                  style={{ background: 'rgba(255,255,255,0.03)' }}
                >
                  <IconBook className="w-8 h-8 text-white/20" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">No courses enrolled yet</h3>
                <p className="text-white/40 text-sm mb-6">Start your learning journey by enrolling in a course</p>
                <Link 
                  to="/courses" 
                  className="px-6 py-3 font-semibold text-sm text-white rounded-xl
                           transition-all duration-300 hover:scale-105"
                  style={{ 
                    background: 'linear-gradient(135deg, #dc464a 0%, #c63b3f 100%)',
                    boxShadow: '0 4px 20px rgba(220, 70, 74, 0.3)'
                  }}
                >
                  Browse Courses
                </Link>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Right Sidebar */}
      <motion.div 
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="hidden xl:flex w-80 flex-col gap-6 flex-shrink-0"
      >
        {/* My Profile Card */}
        <div 
          className="rounded-2xl p-6 flex-shrink-0 relative"
          style={{ 
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.04)'
          }}
        >
          <h3 className="font-semibold text-white mb-6">My profile</h3>
          
          <div className="text-center">
            {user?.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.firstName}
                className="w-24 h-24 rounded-full mx-auto object-cover ring-4 ring-white/10"
              />
            ) : (
              <div 
                className="w-24 h-24 rounded-full mx-auto flex items-center justify-center text-3xl font-bold text-white ring-4 ring-white/10"
                style={{ background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)' }}
              >
                {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
              </div>
            )}
            <h4 className="mt-5 text-lg font-semibold text-white">
              {user?.firstName} {user?.lastName}
            </h4>
            <p className="text-sm text-white/40 flex items-center justify-center gap-1.5 mt-1">
              {user?.role === 'PROFESSOR' ? 'Instructor' : 'Student'} 
              <span className="text-amber-400">⭐</span>
            </p>
          </div>
          
          {/* Activity Section */}
          <div className="mt-6 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <h4 className="text-sm font-medium text-white/50 mb-4">Activity</h4>
            <ActivityChart 
              data={activityData?.dailyActivity || []}
              totalHours={activityData?.totalHours || 0}
              isLoading={isActivityLoading}
            />
          </div>
        </div>

        {/* Upcoming Sessions */}
        <div 
          className="flex-1 rounded-2xl p-6 flex flex-col min-h-0"
          style={{ 
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.04)'
          }}
        >
          <div className="flex items-center justify-between mb-5 flex-shrink-0">
            <h3 className="font-semibold text-white">Live Sessions</h3>
            <button className="p-1.5 hover:bg-white/5 rounded-lg transition-colors">
              <IconMoreVertical className="w-4 h-4 text-white/30" />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {upcomingMeetings.length > 0 ? (
              <div className="space-y-3">
                {upcomingMeetings.map((meeting) => {
                  const isLive = meeting.status === 'LIVE';
                  const meetingDate = new Date(meeting.scheduledAt);
                  const isToday = new Date().toDateString() === meetingDate.toDateString();
                  
                  return (
                    <div 
                      key={meeting.id}
                      className={`p-4 rounded-xl transition-colors ${
                        isLive 
                          ? 'bg-gradient-to-r from-red-500/15 to-purple-500/10 border border-red-500/30' 
                          : 'bg-white/[0.02]'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {isLive && (
                              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-500/20 text-red-400 animate-pulse">
                                <IconRadio className="w-2.5 h-2.5" />
                                LIVE
                              </span>
                            )}
                            {meeting.meetingType === 'QA_FINALE' && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-500/20 text-purple-300">
                                Q&A
                              </span>
                            )}
                          </div>
                          <h4 className="font-semibold text-white text-sm truncate">
                            {meeting.title}
                          </h4>
                          <p className="text-xs text-white/40 mt-0.5 truncate">
                            {meeting.course.title}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 text-xs text-white/40 mb-3">
                        <IconCalendar className="w-3 h-3" />
                        <span>
                          {isToday ? 'Today' : meetingDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {meetingDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      
                      {isLive ? (
                        <button
                          onClick={async () => {
                            try {
                              await joinMeetingSecure(meeting.id);
                            } catch {
                              window.open(buildJitsiJoinUrl(meeting.jitsiRoomId), '_blank');
                            }
                          }}
                          className="w-full py-2 px-3 rounded-lg bg-red-500 text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-red-600 transition-colors"
                        >
                          <IconRadio className="w-4 h-4" />
                          Join Now
                        </button>
                      ) : (
                        <div className="p-2 rounded-lg bg-white/[0.03] text-center">
                          <p className="text-xs text-white/40">Waiting for professor to start</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: 'rgba(255,255,255,0.03)' }}
                >
                  <IconCalendar className="w-6 h-6 text-white/20" />
                </div>
                <p className="text-sm text-white/30">No upcoming sessions</p>
                <p className="text-xs text-white/20 mt-1">Live sessions will appear here</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
